/**
 * useLayers.js
 * Manages rendering of layers in multiple modes: markers, clusters, heatmap.
 */
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.heat';
import { buildClusterOptions } from '../utils/clusterOptions';
import { defaultIcon } from '../utils/markerFactory';

/** Yield to the browser between chunks. */
function nextFrame() {
  return new Promise(resolve => requestAnimationFrame(resolve));
}

/**
 * @param {L.Map}     map
 * @param {object}    popupManager  - from usePopups
 * @param {Function}  emit          - component's $emit
 */
export function createLayerManager(map, popupManager, emit = () => {}) {
  let currentMode = 'markers'; // 'markers' | 'clusters' | 'heatmap'

  // layerId → L.LayerGroup | L.MarkerClusterGroup | L.HeatLayer
  const groups = {};
  // markerId → L.Marker or dummy object
  const markerCache = {};

  // ── Group management ────────────────────────────────────────────────────────

  function getOrCreateGroup(layer) {
    if (!groups[layer.id]) {
      if (currentMode === 'clusters') {
        groups[layer.id] = L.markerClusterGroup(buildClusterOptions(layer.color));
      } else if (currentMode === 'heatmap') {
        groups[layer.id] = L.heatLayer([], { 
          radius: 35, 
          blur: 25, 
          maxZoom: 12,
          minOpacity: 0.4,
          gradient: { 0.2: '#8b5cf6', 0.4: '#3b82f6', 0.6: '#10b981', 0.8: '#f59e0b', 1.0: '#ef4444' }
        });
      } else {
        groups[layer.id] = L.layerGroup();
      }
    }
    return groups[layer.id];
  }

  // ── Marker builder ──────────────────────────────────────────────────────────

  function buildLeafletMarker(markerObj, layerColor) {
    const icon = markerObj.icon || defaultIcon(markerObj.layer, layerColor);
    const lm   = L.marker([markerObj.lat, markerObj.lng], { icon });

    lm._sarpaMarkerId = markerObj.id;
    lm._sarpaLayerId  = markerObj.layer;

    if (markerObj.popup) {
      lm.bindPopup(() => {
        const el = popupManager.mount(markerObj.popup, markerObj.data, markerObj.id);
        emit('marker-click', { marker: markerObj, data: markerObj.data, layer: markerObj.layer });
        return el;
      }, { maxWidth: 300, minWidth: 260, className: 'sarpa-leaflet-popup' });
    }

    lm.on('mouseover', () => {
      emit('marker-hover', { marker: markerObj, data: markerObj.data, layer: markerObj.layer });
    });

    return lm;
  }

  // ── Mode Switcher ───────────────────────────────────────────────────────────

  function setDisplayMode(mode, layers) {
    if (mode === currentMode) return;
    destroy(); // clear map
    currentMode = mode;
    
    // wipe internal state so everything rebuilds fresh for the new mode
    Object.keys(groups).forEach(k => delete groups[k]);
    Object.keys(markerCache).forEach(k => delete markerCache[k]);
    
    updateLayers(layers);
  }

  // ── Main update function ────────────────────────────────────────────────────

  async function updateLayers(layers) {
    for (const layer of layers) {
      const group = getOrCreateGroup(layer);

      if (currentMode === 'heatmap') {
        // Heatmap sets data directly (very fast, no DOM elements)
        const latlngs = layer.markers.map(m => [m.lat, m.lng]);
        group.setLatLngs(latlngs);
        
        // Cache dummy objects just for bounds logic
        layer.markers.forEach(mo => {
          markerCache[mo.id] = {
            _sarpaLayerId: layer.id,
            getLatLng: () => L.latLng(mo.lat, mo.lng)
          };
        });
      } else {
        // Diffing for markers/clusters
        const existingIds = new Set(
          Object.keys(markerCache).filter(id => markerCache[id]._sarpaLayerId === layer.id)
        );
        const incomingIds = new Set(layer.markers.map(m => m.id));

        // Removes
        existingIds.forEach(id => {
          if (!incomingIds.has(id)) {
            group.removeLayer(markerCache[id]);
            delete markerCache[id];
          }
        });

        // Adds (Chunked to prevent freezing)
        const toAdd = layer.markers.filter(m => !existingIds.has(m.id));
        if (toAdd.length > 0) {
          const CHUNK = 100;
          for (let i = 0; i < toAdd.length; i += CHUNK) {
            const chunk = toAdd.slice(i, i + CHUNK);
            const lMarkers = chunk.map(mo => {
              const lm = buildLeafletMarker(mo, layer.color);
              markerCache[mo.id] = lm;
              return lm;
            });

            if (typeof group.addLayers === 'function') {
              group.addLayers(lMarkers);
            } else {
              lMarkers.forEach(lm => group.addLayer(lm));
            }
            await nextFrame();
          }
        }
      }

      // Apply visibility
      if (layer.visible) {
        if (!map.hasLayer(group)) map.addLayer(group);
      } else {
        if (map.hasLayer(group)) map.removeLayer(group);
      }
    }
  }

  // ── Toggle (called from filter panel) ──────────────────────────────────────

  function toggle(layerId, visible) {
    const group = groups[layerId];
    if (!group) return;
    if (visible) {
      if (!map.hasLayer(group)) map.addLayer(group);
    } else {
      if (map.hasLayer(group)) map.removeLayer(group);
    }
    emit('layer-toggle', { layerId, visible });
  }

  // ── Bounds helper ───────────────────────────────────────────────────────────

  function getAllVisibleLatLngs() {
    const result = [];
    Object.values(markerCache).forEach(lm => {
      const groupId = lm._sarpaLayerId;
      const group   = groups[groupId];
      if (group && map.hasLayer(group)) {
        result.push(lm.getLatLng());
      }
    });
    return result;
  }

  // ── Destroy ─────────────────────────────────────────────────────────────────

  function destroy() {
    Object.values(groups).forEach(g => {
      if (map.hasLayer(g)) map.removeLayer(g);
    });
  }

  return { updateLayers, toggle, getAllVisibleLatLngs, destroy, setDisplayMode };
}
